import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import Loader from "@/app/components/Loader";
import { useAuth } from "@/app/context/Auth/AuthContext";

import FitCheckLoading from "./components/FitCheckLoading";
import FitReport from "./components/FitReport";
import SelectDSP from "./components/SelectDSP";
import { FitAnswer, FitAssessment, useAnswers, useAssessFit, useAssessment, useSaveAnswers } from "./config/api";
import TailorResumeDialog from "./tailored/TailorResumeDialog";

export type STEPS = "SELECT_DSP" | "LOADING" | "REPORT" | "UNREADABLE";

interface Company {
	_id: string;
	company_name: string;
	linkedin_url: string;
	logo_url: string;
	company_website: string;
	company_industry: string;
	company_size_approx: string;
	headquarters: string;
	type: string;
	locations: string[];
	about_us_summary: string;
}

interface DateInfo {
	day: number;
	month: number;
	year: number;
}

interface JobLocation {
	city: string;
	region: string;
	country: string;
}

interface QualificationItem {
	bp: boolean;
	value: string;
	_id?: string;
}

interface JobDescription {
	lookingToHire: string;
	responsibilities: QualificationItem[];
	qualifications: QualificationItem[];
}

interface ExperienceQualification {
	minimumLevelOfExperience: string;
	preferredYearsOfExperience: number;
}

interface EducationQualification {
	minEducationLevel: string;
	preferredEducationLevel: string;
	relevantFieldOfStudies: string[];
	relevantMajors: string[];
}

interface Salary {
	payOption: string;
	minPay: number;
	maxPay: number;
	payCurrency: string;
	payFrequency: string;
}

export interface JobDetails {
	_id: string;
	jobProcessingType: string;
	companyName: string;
	company: Company;
	jobTitle: string;
	startingDate: DateInfo;
	deadlineDate: DateInfo;
	numberOfOpening: number;
	jobStreetAddress: string;
	jobLocation: JobLocation;
	employmentType: string;
	jobExpertise: string;
	jobDescription: JobDescription;
	experienceQualification: ExperienceQualification;
	skillsQualifications: QualificationItem[];
	educationQualification: EducationQualification;
	salary: Salary;
	jobStatus: string;
	additionalInformation: string;
	applicationProcess: string;
	lastUpdatedAt: string;
	closeAt: string;
}

interface FitCheckProps {
	jobDetails: JobDetails;
}

function countMustHaves(assessment: FitAssessment) {
	const categories = [assessment.experience, assessment.skills, assessment.education, assessment.certifications];
	let total = 0;
	let met = 0;
	for (const category of categories) {
		for (const item of category.items) {
			if (item.necessity !== "required") continue;
			total += 1;
			if (item.status === "Fit") met += 1;
		}
	}
	return { met, total };
}

function FitCheck({ jobDetails }: FitCheckProps) {
	const [currentStep, setCurrentStep] = useState<STEPS>("SELECT_DSP");
	const [assessment, setAssessment] = useState<FitAssessment | null>(null);
	const [answers, setAnswers] = useState<FitAnswer[]>([]);
	const [tailorOpen, setTailorOpen] = useState(false);

	const { userData } = useAuth();
	const userId = userData?.userID || "";

	const jobProps = {
		jobId: jobDetails._id,
		jobTitle: jobDetails.jobTitle,
		companyName: jobDetails.company?.company_name,
		companyId: jobDetails.company?._id,
	};

	const { data: existing, isLoading: existingLoading } = useAssessment(userId, jobDetails._id);
	const { data: savedAnswers } = useAnswers(userId, jobDetails._id);

	useEffect(() => {
		if (existingLoading) return;
		if (existing?.assessment) {
			setAssessment(existing.assessment);
			setCurrentStep("REPORT");
		}
	}, [existing, existingLoading]);

	useEffect(() => {
		if (savedAnswers && savedAnswers.length > 0) setAnswers(savedAnswers);
	}, [savedAnswers]);

	const { mutate: assessFit } = useAssessFit();
	const { mutate: saveAnswers } = useSaveAnswers();

	const handleComplete = (finalAnswers: FitAnswer[]) => {
		if (userId) saveAnswers({ userId, jobId: jobDetails._id, answers: finalAnswers });
		posthog.capture("fitcheck_questions_completed", {
			...jobProps,
			total: finalAnswers.length,
			haveCount: finalAnswers.filter((a) => a.answer === "have").length,
			lackCount: finalAnswers.filter((a) => a.answer === "lack").length,
		});
	};

	const handleUpdateCv = () => {
		posthog.capture("tailored_resume_opened", {
			...jobProps,
			haveCount: answers.filter((a) => a.answer === "have").length,
			lackCount: answers.filter((a) => a.answer === "lack").length,
		});
		setTailorOpen(true);
	};

	function runFitCheck(resumeURL: string) {
		setCurrentStep("LOADING");
		posthog.capture("fitcheck_started", jobProps);

		assessFit(
			{ resumeURL, userId, jobDetails: jobDetails as unknown as Record<string, unknown> },
			{
				onSuccess: (response) => {
					if (!response.readable || !response.assessment) {
						setCurrentStep("UNREADABLE");
						posthog.capture("fitcheck_unreadable", jobProps);
						return;
					}

					setAssessment(response.assessment);
					setAnswers([]);
					setCurrentStep("REPORT");

					const { met, total } = countMustHaves(response.assessment);
					posthog.capture("fitcheck_completed", {
						jobId: jobDetails._id,
						jobTitle: jobDetails.jobTitle,
						companyName: jobDetails.company?.company_name,
						companyId: jobDetails.company?._id,
						mustHavesMet: met,
						mustHavesTotal: total,
					});
				},
				onError: (error: Error) => {
					setCurrentStep("SELECT_DSP");
					const errorMessage =
						(error as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message ||
						"Failed to run FitCheck. Please try again.";
					posthog.capture("fitcheck_failed", { ...jobProps, message: errorMessage });
					toast.error(errorMessage);
				},
			},
		);
	}

	if (existingLoading && !assessment) {
		return (
			<div className="flex items-center justify-center py-10">
				<Loader />
			</div>
		);
	}

	return (
		<div>
			<div className="px-5 pt-4 pb-1">
				<h3 className="font-plus-jakarta text-[15px] font-semibold text-gray-900">Check your fit</h3>
			</div>
			{currentStep === "SELECT_DSP" && (
				<div className="px-5 py-5">
					<SelectDSP runFitCheck={runFitCheck} />
				</div>
			)}
			{currentStep === "LOADING" && <FitCheckLoading />}
			{currentStep === "REPORT" && assessment && (
				<FitReport
					assessment={assessment}
					answers={answers}
					onAnswersChange={setAnswers}
					onComplete={handleComplete}
					onChangeResume={() => setCurrentStep("SELECT_DSP")}
					onUpdateCv={handleUpdateCv}
					jobId={jobDetails._id}
					jobTitle={jobDetails.jobTitle}
				/>
			)}
			{currentStep === "UNREADABLE" && (
				<div className="px-5 py-5">
					<div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
						<p className="text-sm font-semibold text-gray-900">We could not read that resume</p>
						<p className="mt-1 text-sm text-gray-600">
							The file did not open as readable text. Try another resume, or re-upload this one.
						</p>
						<button
							onClick={() => setCurrentStep("SELECT_DSP")}
							className="mt-4 inline-flex h-9 items-center rounded bg-primary-700 px-4 text-sm font-medium text-white transition-colors hover:bg-primary-800"
						>
							Choose another resume
						</button>
					</div>
				</div>
			)}
			<TailorResumeDialog
				open={tailorOpen}
				onOpenChange={setTailorOpen}
				userId={userId}
				jobId={jobDetails._id}
				jobDetails={jobDetails as unknown as Record<string, unknown>}
				roleTitle={jobDetails.jobTitle}
			/>
		</div>
	);
}

export default FitCheck;
